import { useContext } from 'react';
import { Navigate } from 'react-router-dom';
import AuthContext from '../../context/authContext';
import { Box } from '@mui/material';
import EditableList from '../EditableList/EditableList';
import { viewRootTextPrimarySx } from '../../util/chartTheme';
import UpgradeConfigList from '../EditableList/EditableDisplayElements/UpgradeConfigList';

const formatUpgradeConfigList = (value, item, apiOptions, fieldSchema) => {
  const entries = Array.isArray(value) ? value : [];
  if (entries.length === 0) return '—';

  const optionsKey = fieldSchema?.apiOptionsKey || fieldSchema?.fieldKey;
  const optionSource = optionsKey ? apiOptions?.[optionsKey] : [];

  const formattedEntries = entries
    .map(entry => {
      const upgradeRef = entry?.upgradeId;
      const upgradeId =
        upgradeRef && typeof upgradeRef === 'object'
          ? upgradeRef._id || upgradeRef.id || upgradeRef.value
          : upgradeRef;
      const optionMatch = (Array.isArray(optionSource) ? optionSource : []).find(
        opt => String(opt?.value) === String(upgradeId)
      );
      const label =
        entry?.name ||
        optionMatch?.displayLabel ||
        optionMatch?.label ||
        optionMatch?.name ||
        (upgradeRef && typeof upgradeRef === 'object'
          ? upgradeRef.displayLabel ||
            upgradeRef.label ||
            upgradeRef.name ||
            upgradeRef.longName ||
            upgradeRef._id ||
            upgradeRef.id
          : upgradeRef);

      if (!label && !upgradeId) return null;

      const meta = [];
      if (entry?.activeInThisProduct === false) meta.push('inaktiv');
      if (entry?.sortKey !== undefined && entry?.sortKey !== null) {
        meta.push(`Sortierung ${entry.sortKey}`);
      }
      if (
        entry?.priceOverride !== undefined &&
        entry?.priceOverride !== null &&
        entry?.priceOverride !== ''
      ) {
        meta.push(`Preis ${entry.priceOverride}`);
      }

      const finalLabel = label || String(upgradeId);
      return meta.length > 0 ? `${finalLabel} (${meta.join(', ')})` : finalLabel;
    })
    .filter(Boolean);

  return formattedEntries.join(' | ') || '—';
};

function ResourceVoucherProductManagement() {
  const authCtx = useContext(AuthContext);

  if (!authCtx.checkRight('resourceVoucherProduct:read')) {
    return <Navigate to='/welcome' replace />;
  }

  const arrayDisplayValue = value => (Array.isArray(value) ? value.join(', ') : '—');
  const arrayTransformValue = value =>
    value
      .toString()
      .split(',')
      .map(v => v.trim())
      .filter(Boolean);

  const schema = {
    name: { label: 'Name', type: 'text', required: true, minWidth: '220px' },
    description: {
      label: 'Beschreibung',
      type: 'text',
      required: false,
      minWidth: '360px',
      previewInList: true,
      previewMaxLines: 3,
      useHyphens: true,
    },
    branchId: {
      label: 'Filiale',
      type: 'select',
      api: {
        fieldName: 'branch',
        itemField: 'branchId',
        idName: '_id',
        resource: 'branch',
        labelField: 'longName',
        valueField: '_id',
        skipSpecial: true,
      },
      placeholder: 'Filiale auswählen',
      required: true,
      minWidth: '180px',
    },
    internalDescription: {
      label: 'Interne Beschreibung',
      type: 'text',
      required: false,
      minWidth: '360px',
      previewInList: false,
      previewMaxLines: 3,
      useHyphens: true,
    },
    includedUpsellIds: {
      label: 'Inkludierte Upsells',
      type: 'multi-select',
      api: {
        resource: 'upgrade',
        idName: '_id',
        labelField: 'name',
        valueField: '_id',
        skipSpecial: true,
      },
      placeholder: 'Upsells auswählen (werden bei Einlösung automatisch hinzugefügt)',
      required: false,
      minWidth: '260px',
    },
    durationMS: { label: 'Dauer (ms)', type: 'number', required: true, minWidth: '140px' },
    personCount: { label: 'Personen', type: 'number', required: true, minWidth: '120px' },
    categories: {
      label: 'Kategorien',
      type: 'nested',
      addButtonText: 'Kategorie hinzufügen',
      excludeFromAdd: true,
      api: { itemField: 'categories', nestedEndpoint: 'categories' },
      schema: {
        name: { label: 'Name', type: 'text', required: true },
        shortName: { label: 'Kurzname', type: 'text', required: false },
        description: { label: 'Beschreibung', type: 'text', required: false },
      },
    },
    ageSplitMapping: {
      label: 'Altersgruppen-Zuordnung',
      type: 'object',
      schema: {
        kids: {
          label: 'Kinder-Kategorien',
          type: 'array',
          placeholder: 'z.B. U10, U14',
          displayValue: arrayDisplayValue,
          transformValue: arrayTransformValue,
        },
        adults: {
          label: 'Erwachsenen-Kategorien',
          type: 'array',
          placeholder: 'z.B. Ü14, Erwachsene',
          displayValue: arrayDisplayValue,
          transformValue: arrayTransformValue,
        },
      },
    },
    packages: {
      label: 'Pakete',
      type: 'upgrade-config-list',
      excludeFromAdd: true,
      fieldKey: 'packages',
      apiOptionsKey: 'packages',
      listDisplayValue: formatUpgradeConfigList,
      previewInList: true,
      previewMaxLines: 2,
      api: {
        resource: 'upgrade',
        idName: '_id',
        labelField: 'name',
        valueField: '_id',
        skipSpecial: true,
      },
    },
    upsells: {
      label: 'Upsells',
      type: 'upgrade-config-list',
      excludeFromAdd: true,
      fieldKey: 'upsells',
      apiOptionsKey: 'upsells',
      listDisplayValue: formatUpgradeConfigList,
      previewInList: true,
      previewMaxLines: 2,
      api: {
        resource: 'upgrade',
        idName: '_id',
        labelField: 'name',
        valueField: '_id',
        skipSpecial: true,
      },
    },
    resourceIds: {
      label: 'Ressourcen',
      type: 'multi-select',
      api: {
        resource: 'resource',
        idName: '_id',
        labelField: 'name',
        valueField: '_id',
        skipSpecial: true,
      },
      placeholder: 'Ressourcen auswählen',
      required: false,
      minWidth: '220px',
    },
    buyableFrom: { label: 'Kaufbar ab', type: 'date', required: false, minWidth: '160px' },
    buyableTo: { label: 'Kaufbar bis', type: 'date', required: false, minWidth: '160px' },
    isActive: { label: 'Aktiv', type: 'boolean', required: false },
    validityType: {
      label: 'Gültigkeit',
      type: 'select',
      required: true,
      options: [
        { value: 'german3years', label: '3 Jahre (DE)' },
        { value: 'custom', label: 'Benutzerdefiniert (Tage)' },
      ],
    },
    validityDays: { label: 'Gültigkeitstage (bei custom)', type: 'number', required: false },
    maxPerEmail: { label: 'Max. pro E-Mail', type: 'number', required: false },
    basePrice: { label: 'Basispreis', type: 'price', required: true },
    originalPrice: { label: 'Originalpreis', type: 'price', required: false },
    priceRules: {
      label: 'Preisregeln',
      type: 'nested',
      addButtonText: 'Regel hinzufügen',
      excludeFromAdd: true,
      api: { itemField: 'priceRules', nestedEndpoint: 'priceRules' },
      schema: {
        startDate: { label: 'Startdatum', type: 'date', required: true },
        endDate: { label: 'Enddatum', type: 'date', required: true },
        type: {
          label: 'Typ',
          type: 'select',
          required: true,
          options: [
            { value: 'fixed', label: 'Fix' },
            { value: 'discountPercent', label: 'Rabatt %' },
          ],
        },
        value: { label: 'Wert', type: 'price', required: true },
      },
    },
    tieredPricing: {
      label: 'Staffelpreise',
      type: 'nested',
      addButtonText: 'Stufe hinzufügen',
      excludeFromAdd: true,
      api: { itemField: 'tieredPricing', nestedEndpoint: 'tieredPricing' },
      schema: {
        minQuantity: { label: 'Ab Anzahl', type: 'number', required: true },
        type: {
          label: 'Typ',
          type: 'select',
          required: true,
          options: [
            { value: 'fixed', label: 'Fix' },
            { value: 'discountPercent', label: 'Rabatt %' },
          ],
        },
        value: { label: 'Wert', type: 'price', required: true },
      },
    },
  };

  return (
    <Box sx={viewRootTextPrimarySx}>
    <EditableList
      schema={schema}
      title='Gutscheinprodukt verwalten'
      resourceType='resourceVoucherProduct'
      parentIsEditable={authCtx.checkRight('resourceVoucherProduct:update')}
      visibleColumnKeys={[
        'name',
        'description',
        'branchId',
        'internalDescription',
        'includedUpsellIds',
        'durationMS',
        'personCount',
        'categories',
        'ageSplitMapping',
        'resourceIds',
        'buyableFrom',
        'buyableTo',
        'isActive',
        'validityType',
        'validityDays',
        'maxPerEmail',
        'basePrice',
        'originalPrice',
        'priceRules',
        'tieredPricing',
      ]}
      options={{
        titleKey: 'name',
        disableInlineEditing: true,
        customRenderers: {
          'upgrade-config-list': UpgradeConfigList,
        },
      }}
    />
    </Box>
  );
}

export default ResourceVoucherProductManagement;
